import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import moment from "moment";
import {
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Center,
} from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";

import { useServerApi } from "../hooks/userServerApi";
import { useCellRender } from "../hooks/useCellRender";
import { useFormatter } from "../hooks/useFomatter";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { resolveEntityPath } from "../utils/resolveEntityPath";
import { EDD_INDICATOR_LABELS } from "../helpers/eddIndicatorLabels";

const ClientOrderSummary = ({ form, mode = "edd" }) => {
  const clientId = _.get(form.values, "_id");
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [cellRender] = useCellRender();
  const [formatter] = useFormatter();
  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const { t } = useTranslation();
  const orderLinkPath = resolveEntityPath("order", auth()?.userRole);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const openOrder = (e, order) => {
    e.preventDefault();
    const id = order?._id || order?.orderId;
    if (!id || !orderLinkPath) return;
    const code = order.code || order.orderCode;
    if (tabNav.enabled) {
      tabNav.openDetail({
        pathname: orderLinkPath,
        id,
        mode: "edit",
        label: code,
      });
      return;
    }
    window.open(`${orderLinkPath}?id=${id}&mode=edit`, "_blank");
  };

  const fetchOrderSummary = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const result = await apiRef.current.Client.getOrderSummary(clientId);
      if (result?.success) {
        setSummary(result.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderSummary();
  }, [clientId]);

  if (!clientId) {
    return (
      <Text size="xs" color="dimmed">
        {t("Save the client record to view order summary.")}
      </Text>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!summary) {
    return (
      <Text size="xs" color="dimmed">
        {t("No order summary data available.")}
      </Text>
    );
  }

  const life = summary.lifeIlas || {};
  const subscription = summary.subscription || {};
  const withdrawal = summary.withdrawal || {};
  const money = (value) => cellRender.Currency(value, "HKD");
  const moneyText = (value) => formatter.currency(value, "HKD");

  const indicatorRows = [
    {
      key: "lifeNb",
      on: !!life.nbCountFlag,
      label: EDD_INDICATOR_LABELS.lifeNb,
      detail: `${life.nbCount12Months ?? 0} ${t(
        "new business cases in the last 12 months"
      )}`,
    },
    {
      key: "lifeLumpSum",
      on: !!life.lumpSumFlag,
      label: EDD_INDICATOR_LABELS.lifeLumpSum,
      detail: `${moneyText(life.lumpSumPremiumHkd)} · ${t("Single premium")} ${moneyText(
        life.singlePremiumHkd
      )} + ${t("Annual premium equivalent")} ${moneyText(
        life.annualPremiumEquivalentHkd
      )}`,
    },
    {
      key: "subscriptionHigh",
      on: !!subscription.highAmountFlag,
      label: EDD_INDICATOR_LABELS.subscriptionHigh,
      detail: `${subscription.count ?? 0} ${t("subscriptions")} · ${moneyText(
        subscription.totalAmountHkd
      )}`,
    },
    {
      key: "subscriptionFrequent",
      on: !!subscription.frequentFlag,
      label: EDD_INDICATOR_LABELS.subscriptionFrequent,
      detail: `${subscription.count ?? 0} ${t("subscriptions")} · ${moneyText(
        subscription.totalAmountHkd
      )}`,
    },
    {
      key: "withdrawalTop5",
      on: !!withdrawal.inTop5,
      label: EDD_INDICATOR_LABELS.withdrawalTop5,
      detail: withdrawal.inTop5
        ? `${t("Rank")} ${withdrawal.rank} · ${withdrawal.month || ""}`
        : `${withdrawal.transactions?.length ?? 0} ${t(
            "withdrawals this month"
          )} · ${withdrawal.month || ""}`,
    },
  ];
  const flaggedCount = indicatorRows.filter((row) => row.on).length;
  const passedCount = indicatorRows.length - flaggedCount;

  const figures = [
    {
      label: "Life/ILAS NB (12 months)",
      value: String(life.nbCount12Months ?? 0),
    },
    {
      label: "Lump sum premium",
      value: money(life.lumpSumPremiumHkd),
    },
    {
      label: "Total premium",
      value: money(summary.totalPremiumHkd),
    },
  ];

  const sumInsuredSection = (
    <>
      <Title order={6} mb="sm">
        {t("Sum Insured by Business Line")}
      </Title>
      {_.isEmpty(summary.sumInsuredByBusinessLine) ? (
        <Text size="sm" color="dimmed">
          {t("No sum insured data available.")}
        </Text>
      ) : (
        <Table fontSize="sm" striped highlightOnHover>
          <thead>
            <tr>
              <th>{t("Business Line")}</th>
              <th>{t("Orders")}</th>
              <th>{t("Total Sum Insured (HKD)")}</th>
              <th>{t("Total Premium (HKD)")}</th>
            </tr>
          </thead>
          <tbody>
            {summary.sumInsuredByBusinessLine.map((row) => (
              <tr key={row.businessLine}>
                <td>{row.businessLine}</td>
                <td>{row.orderCount}</td>
                <td>{money(row.totalSumInsuredHkd)}</td>
                <td>{money(row.totalPremiumHkd)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  if (mode === "summary") return sumInsuredSection;

  return (
    <>
      <Title order={5} mb={4}>
        {t("EDD")}
      </Title>
      <Text size="xs" color="dimmed" mb="md">
        {t(
          "Life/ILAS lump-sum premium is single premium plus annual premium equivalent. Subscription figures cover the last 12 months."
        )}
      </Text>

      <Paper withBorder radius="md" p="md" mb="lg">
        <Text size="sm" weight={600}>
          {t("Indicator result")}
        </Text>
        <Text size="xs" color="dimmed" mb="md">
          {t("{{passed}} passed, {{flagged}} flagged, out of {{total}}", {
            passed: passedCount,
            flagged: flaggedCount,
            total: indicatorRows.length,
          })}
        </Text>
        <SimpleGrid cols={3} mb="xs">
          <Stack spacing={0}>
            <Text size="xl" weight={700}>
              {indicatorRows.length}
            </Text>
            <Text size="xs" color="dimmed">
              {t("Indicators")}
            </Text>
          </Stack>
          <Stack spacing={0}>
            <Text size="xl" weight={700} color="teal">
              {passedCount}
            </Text>
            <Text size="xs" color="teal">
              {t("Passed")}
            </Text>
          </Stack>
          <Stack spacing={0}>
            <Text size="xl" weight={700} color="orange">
              {flaggedCount}
            </Text>
            <Text size="xs" color="orange">
              {t("Flagged")}
            </Text>
          </Stack>
        </SimpleGrid>

        {indicatorRows.map((row) => (
          <Group
            key={row.key}
            position="apart"
            align="flex-start"
            noWrap
            py="sm"
            sx={(theme) => ({
              borderTop: `1px solid ${theme.colors.gray[3]}`,
            })}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Text size="sm">{t(row.label)}</Text>
              <Text size="xs" color="dimmed" mt={2}>
                {row.detail}
              </Text>
            </Box>
            <Badge
              color={row.on ? "orange" : "teal"}
              variant="filled"
              styles={{
                root: { lineHeight: 1 },
                leftSection: {
                  display: "flex",
                  alignItems: "center",
                },
                inner: { lineHeight: 1 },
              }}
              leftSection={
                row.on ? (
                  <IconAlertTriangle size={12} style={{ display: "block" }} />
                ) : (
                  <IconCircleCheck size={12} style={{ display: "block" }} />
                )
              }
            >
              {row.on ? t("Flagged") : t("Passed")}
            </Badge>
          </Group>
        ))}
      </Paper>

      <SimpleGrid
        cols={3}
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        mb="xl"
      >
        {figures.map((figure) => (
          <Paper key={figure.label} withBorder radius="md" p="md">
            <Text size="xs" color="dimmed" mb={4}>
              {t(figure.label)}
            </Text>
            <Text size="lg" weight={600}>
              {figure.value}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      <Title order={6} mb="sm">
        {t("Life/ILAS new business in the lump-sum total")}
      </Title>
      {_.isEmpty(life.orders) ? (
        <Text size="sm" color="dimmed" mb="xl">
          {t("No Life or ILAS new business orders.")}
        </Text>
      ) : (
        <Table fontSize="sm" striped highlightOnHover mb="xl">
          <thead>
            <tr>
              <th>{t("Order")}</th>
              <th>{t("Business Line")}</th>
              <th>{t("Signed Date")}</th>
              <th>{t("Premium Role")}</th>
              <th>{t("Premium (HKD)")}</th>
              <th>{t("Status")}</th>
            </tr>
          </thead>
          <tbody>
            {life.orders.map((order) => (
              <tr key={order._id}>
                <td>
                  <Text
                    component="a"
                    style={{ textDecoration: "none", cursor: "pointer" }}
                    onClick={(e) => openOrder(e, order)}
                  >
                    {order.code}
                  </Text>
                  {order.policyNumber && (
                    <Text color="dimmed" size="xs">
                      {order.policyNumber}
                    </Text>
                  )}
                  <Text color="dimmed" size="xs">
                    {order.productTypeName}
                  </Text>
                </td>
                <td>{order.businessLine}</td>
                <td>
                  {order.signedDate
                    ? moment(order.signedDate).format("YYYY-MM-DD")
                    : "-"}
                  {order.inLast12Months && (
                    <Badge size="xs" ml={6} variant="outline">
                      12M
                    </Badge>
                  )}
                </td>
                <td>
                  {order.premiumRole === "single"
                    ? t("Single")
                    : t("Annual equivalent")}
                </td>
                <td>{money(order.yearPremium)}</td>
                <td>{cellRender.Order.status(order.status)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Title order={6} mb="sm">
        {t("Mutual fund and fixed income subscriptions (12 months)")}
      </Title>
      {_.isEmpty(subscription.transactions) ? (
        <Text size="sm" color="dimmed" mb="xl">
          {t("No subscriptions in the last 12 months.")}
        </Text>
      ) : (
        <Table fontSize="sm" striped highlightOnHover mb="xl">
          <thead>
            <tr>
              <th>{t("Transaction")}</th>
              <th>{t("Order")}</th>
              <th>{t("Date")}</th>
              <th>{t("Amount (HKD)")}</th>
            </tr>
          </thead>
          <tbody>
            {subscription.transactions.map((txn) => (
              <tr key={txn._id}>
                <td>{txn.code || "-"}</td>
                <td>
                  <Text
                    component="a"
                    style={{ textDecoration: "none", cursor: "pointer" }}
                    onClick={(e) =>
                      openOrder(e, { _id: txn.orderId, code: txn.orderCode })
                    }
                  >
                    {txn.orderCode || "-"}
                  </Text>
                </td>
                <td>
                  {txn.date ? moment(txn.date).format("YYYY-MM-DD") : "-"}
                </td>
                <td>{money(txn.amountHkd)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Title order={6} mb="sm">
        {t("Withdrawals this month")} ({withdrawal.month || "-"})
      </Title>
      {_.isEmpty(withdrawal.transactions) ? (
        <Text size="sm" color="dimmed" mb="xl">
          {t("No withdrawals this month.")}
        </Text>
      ) : (
        <Table fontSize="sm" striped highlightOnHover mb="xl">
          <thead>
            <tr>
              <th>{t("Service")}</th>
              <th>{t("Order")}</th>
              <th>{t("Date")}</th>
              <th>{t("Amount")}</th>
            </tr>
          </thead>
          <tbody>
            {withdrawal.transactions.map((row) => (
              <tr key={row._id}>
                <td>{row.code || "-"}</td>
                <td>
                  <Text
                    component="a"
                    style={{ textDecoration: "none", cursor: "pointer" }}
                    onClick={(e) =>
                      openOrder(e, { _id: row.orderId, code: row.orderCode })
                    }
                  >
                    {row.orderCode || "-"}
                  </Text>
                </td>
                <td>{row.date ? moment(row.date).format("YYYY-MM-DD") : "-"}</td>
                <td>{money(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

    </>
  );
};

export default ClientOrderSummary;
